import React, { useState, useEffect, useContext } from 'react';
import { FilterContext } from '../../context/FilterContext';
import { activitiesAPI } from '../../services/api';
import { enqueueRequest, clearQueue } from '../../services/travelTimeQueue';
import { ActivityTopSection, ActivityCategoryList, TravelTimeDisplay, LocationAddress } from './ActivityCommon';

import { Clock, Directions, Share, Wheelchair, WheelchairInactive, Delete, Check_Button, Warning, Error as ErrorIcon } from '../Icons/CustomIcons';
import { Pencil } from 'lucide-react';
import { DoCancelDialog, GoToDialog } from '../Common/Dialog';
import { IconLeftButtonS } from '../Common/Button';

/* ----------------------------------------------------------
   ActivityCard – Grosskinder Aktiv
   Props identisch mit Original:
     activity        {object}  – Aktivitätsobjekt aus der API
     onActivityClick {func}    – Callback bei Klick auf Card
   ---------------------------------------------------------- */

const ActivityCard = ({ activity, onActivityClick, showStatusBanner = false, onEdit, onDelete }) => {
  const { filters } = useContext(FilterContext);
  const [travelTime, setTravelTime] = useState(null);
  const [travelLoading, setTravelLoading] = useState(false);
  const [travelError, setTravelError] = useState(false);
  const [shareFeedback, setShareFeedback] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit?.(activity);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      await activitiesAPI.delete(activity.id);
      setShowDeleteConfirm(false);
      setDeleteSuccess(true);
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handlePlan = (e) => {
    e.stopPropagation();
    onActivityClick?.(activity);
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/activities/${activity.id}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: activity.name, url });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(url);
        }
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setShareFeedback(true);
      setTimeout(() => setShareFeedback(false), 2000);
    } catch (err) {
      console.error('Could not copy URL:', err);
    }
  };

  /* --------------------------------------------------------
     Reisezeit laden – Logik aus Original übernommen
  -------------------------------------------------------- */
  useEffect(() => {
    setTravelTime(null);
    setTravelError(false);

    if (!filters.start_address || !filters.departure_date || !filters.departure_time) {
      return;
    }

    const cacheKey = `travel:${activity.id}:${filters.start_address}:${filters.departure_date}:${filters.departure_time}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached !== null) {
      setTravelTime(JSON.parse(cached));
      return;
    }

    let cancelled = false;
    setTravelLoading(true);

    enqueueRequest(
      () => activitiesAPI.getTravelPlan(activity.id, {
        from_address: filters.start_address,
        departure_date: filters.departure_date,
        departure_time: filters.departure_time,
      }),
      () => cancelled
    ).then(response => {
      if (cancelled || response === null) return;
      if (response.data?.duration_minutes != null && !response.data.no_connection) {
        sessionStorage.setItem(cacheKey, JSON.stringify(response.data.duration_minutes));
        setTravelTime(response.data.duration_minutes);
      } else if (response.data?.no_connection) {
        sessionStorage.setItem(cacheKey, JSON.stringify(0));
        setTravelTime(0);
      } else {
        setTravelError(true);
      }
    }).catch(err => {
      if (cancelled) return;
      console.error('Error fetching travel time:', err);
      setTravelError(true);
    }).finally(() => {
      if (!cancelled) setTravelLoading(false);
    });

    return () => {
      cancelled = true;
      clearQueue();
    };
  }, [filters.start_address, filters.departure_date, filters.departure_time, activity.id]);

  /* --------------------------------------------------------
     Zeitanzeige – Logik aus Original übernommen
  -------------------------------------------------------- */
  const getCardTimeDisplay = () => {
    const { event_type, event_schedule, opening_hours, active_time } = activity;
    const filterDate = filters.departure_date;

    if (event_type === 'freely_accessible') {
      return 'Frei zugänglich';
    }
    if (event_type === 'recurring_event') {
      const start = active_time?.start_time || event_schedule?.start_time;
      const end = active_time?.end_time || event_schedule?.end_time;
      if (!start) return null;
      return end ? `${start} – ${end} Uhr` : `${start} Uhr`;
    }

    if (event_type === 'single_event' && event_schedule) {
      if (active_time?.start_time) {
        const end = active_time.end_time;
        return end ? `${active_time.start_time} – ${end} Uhr` : `${active_time.start_time} Uhr`;
      }
      const dates = event_schedule.dates || [];
      if (dates.length > 0) {
        const match = filterDate ? dates.find(d => d.date === filterDate) : dates[0];
        const entry = match || dates[0];
        if (!entry?.start_time) return null;
        return entry.end_time ? `${entry.start_time} – ${entry.end_time} Uhr` : `${entry.start_time} Uhr`;
      }
      if (event_schedule.start_time) {
        return event_schedule.end_time
          ? `${event_schedule.start_time} – ${event_schedule.end_time} Uhr`
          : `${event_schedule.start_time} Uhr`;
      }
      return null;
    }

    if (event_type === 'multi_day' && event_schedule) {
      const first = (event_schedule.dates || [])[0];
      if (!first?.start_time) return null;
      return first.end_time ? `${first.start_time} – ${first.end_time} Uhr` : `${first.start_time} Uhr`;
    }

    if (event_type === 'regular_hours' && opening_hours && filterDate) {
      const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayName = DAYS[new Date(filterDate).getDay()];
      const hours = opening_hours.find(h => h.day === dayName);
      if (!hours?.opens) return null;
      return hours.closes ? `${hours.opens} – ${hours.closes} Uhr` : `${hours.opens} Uhr`;
    }

    return null;
  };

  const timeLabel = getCardTimeDisplay();
  const categories = Array.isArray(activity.categories) ? activity.categories : [];
  const isPending = activity.status === 'pending';

  const STATUS_BANNER = {
    // eslint-disable-next-line react/jsx-pascal-case
    active:   { text: 'Ist veröffentlicht und sichtbar',        bg: 'rgba(34, 197, 94, 0.15)',   border: '#22c55e', icon: <Check_Button size={18} color="#22c55e" /> },
    pending:  { text: 'In Prüfung durch Moderation',            bg: 'rgba(240, 169, 37, 0.30)',  border: '#F0A925', icon: <Warning size={18} color="#F0A925" /> },
    archived: { text: 'Ist archiviert und nicht mehr sichtbar', bg: 'rgba(107, 114, 128, 0.15)', border: '#6b7280', icon: <ErrorIcon size={18} color="#6b7280" /> },
  };
  const bannerConfig = showStatusBanner ? STATUS_BANNER[activity.status] : (isPending ? STATUS_BANNER.pending : null);

  return (
    <>
    <article
      className="ac-event-card"
      onClick={() => onActivityClick?.(activity)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onActivityClick?.(activity)}
    >
      {/* Status-Banner */}
      {bannerConfig && (
        <div className="ac-status-banner" style={{ background: bannerConfig.bg, borderTopColor: bannerConfig.border, borderBottomColor: bannerConfig.border }}>
          {bannerConfig.icon}
          <span className="ac-status-text">{bannerConfig.text}</span>
        </div>
      )}

      {/* Rollstuhl-Badge */}
      {activity.wheelchair_accessible ? (
        <div className="wheelchair" title="Rollstuhlgerecht">
          <Wheelchair size={28} />
        </div>
      ) : (
        <div className="wheelchair wheelchair--inactive" title="Nicht rollstuhlgerecht">
          <WheelchairInactive size={28} color="#ffbfc4" />
        </div>
      )}

      {/* Hauptinhalt */}
      <div className="ac-content">
        <div className="ac-top">

          {/* Uhrzeit or Frei zugänglich */}
          {timeLabel && (
            <div className="ac-datetime">
              <Clock size={20} />
              <span className="body Font-M-S-130-T Colors-Black">{timeLabel}</span>
            </div>
          )}

          <div className="ac-main">
            {/* Titel + Meta-Zeile */}        
            <div className="ac-header">
              <h2 className="headings Font-H2-A-130-T Colors-Black">{activity.name}</h2>
              <ActivityTopSection activity={activity} />
            </div>

            {/* Adresse + Reisezeit */}
            <div className="ac-location-duration">
              <LocationAddress locationName={activity.location_name} address={activity.address} />
              {!bannerConfig && (
                <TravelTimeDisplay
                    travelLoading={travelLoading}
                    travelError={travelError}
                    travelTime={travelTime}
                  />
                )}
              </div>
          </div>
        </div>

        {/* Kategorien */}
        <ActivityCategoryList
          categories={categories}
          className="ac-categories"
          chipClassName="tag tag--category"
        />

        {/* Beschreibung */}
        {activity.description && (
          <p className="body Font-M-B-130-T Colors-Grey-07 ac-description">{activity.description}</p>
        )}
        </div>

        {/* Aktions-Buttons */}
        <div className="ac-actions">
          {bannerConfig ? (
            <>
              <button
                className="btn btn-s btn-primary ac-action-btn ac-action-btn--edit"
                onClick={handleEdit}
                type="button"
                aria-label={`${activity.name} bearbeiten`}
              >
                <Pencil size={20} />
                <span>Bearbeiten</span>
              </button>
              <button
                className="btn btn-s ac-action-btn ac-action-btn--delete"
                onClick={handleDelete}
                type="button"
                aria-label={`${activity.name} löschen`}
              >
                <Delete size={20} />
                <span className="ac-action-btn--text">Aktivität löschen</span>
              </button>
            </>
          ) : (
            <>
              <IconLeftButtonS
                className="btn-secondary ac-action-btn"
                onClick={handlePlan}
                ariaLabel={`Route zu ${activity.name} planen`}
                icon={<Directions size={20} />}
                label="Plan"
              />
              <IconLeftButtonS
                className="btn-secondary ac-action-btn"
                onClick={handleShare}
                ariaLabel={`${activity.name} teilen`}
                icon={<Share size={20} />}
                label={shareFeedback ? 'Kopiert!' : 'Teilen'}
              />
            </>
          )}
      </div>
    </article>

      {showDeleteConfirm && (
        <DoCancelDialog
          icon={Delete}
          title="Aktivität wirklich löschen?"
          text={deleteLoading ? 'Wird gelöscht…' : 'Endgültig löschen'}
          message="Die Aktivität wird dauerhaft gelöscht und kann nicht wiederhergestellt werden."
          onConfirm={handleDeleteConfirm}
          onCancel={() => setShowDeleteConfirm(false)}
          danger
        />
      )}

      {deleteSuccess && (
        <GoToDialog
          icon={Delete}
          title="Aktivität gelöscht."
          text="Schliessen"
          message="Die Aktivität wurde dauerhaft gelöscht."
          onConfirm={() => { setDeleteSuccess(false); onDelete?.(activity); }}
          danger
        />
      )}
    </>
  );
};

export default ActivityCard;
